/**
 * BatchDownloadModal Component
 *
 * Generates a QR code linking to a page where all current assets
 * can be downloaded individually — great for batch saving videos on iPhone.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../contexts/AuthContext';
import './SendToDeviceModal.css'; // Reuse same styles

interface FileInfo {
  name: string;
  url: string;
  type: string;
  size: number;
}

interface BatchDownloadModalProps {
  files: FileInfo[];
  onClose: () => void;
}

const BatchDownloadModal: React.FC<BatchDownloadModalProps> = ({ files, onClose }) => {
  const { user } = useAuth();
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expiresIn, setExpiresIn] = useState(15 * 60);

  useEffect(() => {
    createBatchSession();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (expiresIn <= 0) return;

    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const createBatchSession = async () => {
    setLoading(true);
    setError(null);

    if (!user?.id) {
      setError('Please sign in to use batch download');
      setLoading(false);
      return;
    }

    try {
      const baseUrl = window.location.origin;

      if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
        setError('Cannot generate QR code for local development URLs. Deploy to production or use a tunnel service.');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/media/batch-download/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
        },
        body: JSON.stringify({ files }),
      });

      if (!response.ok) {
        throw new Error('Failed to create batch download session');
      }

      const data = await response.json();
      const batchUrl = `${baseUrl}/batch-download/${data.code}`;
      setDownloadUrl(batchUrl);
      setLoading(false);
    } catch (err) {
      console.error('[BatchDownloadModal] Failed to create session:', err);
      setError('Failed to create download link');
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return ReactDOM.createPortal(
    <div className="send-to-device-overlay" onClick={onClose}>
      <div className="send-to-device-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <PhoneIphoneIcon className="header-icon" />
            <span>Download {files.length} Files</span>
          </div>
          <button className="close-btn" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* File Summary */}
          <div className="asset-info">
            <div className="asset-name">{files.length} file{files.length !== 1 ? 's' : ''} ready to download</div>
            <div className="asset-meta">
              <span className="meta-type">
                {files.filter(f => f.type.startsWith('image/')).length > 0 &&
                  `${files.filter(f => f.type.startsWith('image/')).length} images`}
                {files.filter(f => f.type.startsWith('video/')).length > 0 &&
                  `${files.filter(f => f.type.startsWith('image/')).length > 0 ? ' · ' : ''}${files.filter(f => f.type.startsWith('video/')).length} videos`}
              </span>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="qr-section">
            {loading ? (
              <div className="qr-loading">
                <div className="loading-spinner"></div>
                <span>Generating QR code...</span>
              </div>
            ) : error ? (
              <div className="qr-error">
                <span className="error-icon">&#x26A0;&#xFE0F;</span>
                <span className="error-message">{error}</span>
              </div>
            ) : (
              <>
                <div className="qr-code-container">
                  <QRCodeSVG
                    value={downloadUrl}
                    size={200}
                    level="M"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                <div className="qr-instructions">
                  <span className="instruction-text">
                    Scan with your phone to save these files
                  </span>
                  {expiresIn > 0 && (
                    <span className="expiry-timer">
                      Link expires in {formatTime(expiresIn)}
                    </span>
                  )}
                  {expiresIn === 0 && (
                    <span className="expiry-expired">
                      Link expired - close and reopen to refresh
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Copy Link Button */}
          {!loading && !error && downloadUrl && (
            <button
              className="copy-link-btn"
              onClick={handleCopyLink}
            >
              {copied ? (
                <>
                  <CheckIcon fontSize="small" />
                  Copied!
                </>
              ) : (
                <>
                  <ContentCopyIcon fontSize="small" />
                  Copy Download Link
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BatchDownloadModal;
